import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { config } from '@/config';
import { SecondFactorPrompt } from '@/features/auth/SecondFactorPrompt';
import { MfaChallenge, isMfaChallenge } from '@/features/auth/mfaChallenge';
import { AuthShell } from './signup/AuthShell';
import { completeSignIn } from '@/lib/activeAccount';
import { tooManyAccountsMessage } from '@/features/auth/tooManyAccounts';

/**
 * Where a magic-link email lands (`docs/WebAuthn.md` §4.4).
 *
 * The mailer has always pointed here — `{host}/auth/magic-link/{token}` — while the route did
 * not exist, so every magic-link email dead-ended on the SPA's fallback. This is that route.
 *
 * ⚠ **Redeemed with a POST, from here, not by following the link to the API.** The backend
 * endpoint consumes the token, and a GET that consumes something is spent by any prefetcher,
 * scanner, or mail-client preview that touches the URL — the user would arrive to find their
 * own link already used.
 *
 * **A magic link is a first factor, not a second** (decision 4). It proves control of the
 * mailbox, which is what a password proves control of an account — so a user who holds a
 * security key is still asked for it here. Gated, not retired.
 */
export default function MagicLinkLanding() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [redeemError, setRedeemError] = useState<string | undefined>(undefined);
  const error = token ? redeemError : 'This sign-in link is missing its token.';
  const [challenge, setChallenge] = useState<MfaChallenge | undefined>(undefined);
  // Single-use, and StrictMode double-invokes effects in development: redeeming twice spends
  // the token and the second attempt fails on a link that was perfectly good.
  const redeemed = useRef(false);

  const land = useCallback(async (response: unknown) => {
    if (await completeSignIn(queryClient, response, '/home')) return;
    await navigate('/home');
  }, [queryClient, navigate]);

  useEffect(() => {
    if (redeemed.current || !token) return;
    redeemed.current = true;
    void axios
      .post(`${config.backendApi}/api/auth/magic-link/${token}`, {}, { withCredentials: true })
      .then(
        (res) => {
          if (isMfaChallenge(res.data)) {
            setChallenge(res.data);
            return undefined;
          }
          return land(res.data);
        },
        (e: unknown) => {
          setRedeemError(
            tooManyAccountsMessage(e) ?? 'This sign-in link has expired or was already used. Request a new one.',
          );
        },
      );
  }, [token, land]);

  if (challenge) {
    return (
      <AuthShell>
        <SecondFactorPrompt
          challenge={challenge}
          onCancel={() => { void navigate('/signin'); }}
          onAuthenticated={land}
        />
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="p-6 space-y-4">
        {error ? (
          <>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Link to="/signin" className="text-sm text-primary hover:underline">
              Back to sign in
            </Link>
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Signing you in…
          </div>
        )}
      </div>
    </AuthShell>
  );
}
